import {
  Box,
  Chip,
  Divider,
  Grid,
  IconButton,
  LinearProgress,
  Link,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import Header from "../../common/Header";
import Card from "../../common/Card";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  getCreditMemoDetailsById,
  getInvoiceById,
  InvoiceDetailDTO,
  useInvoiceSlice,
  uploadInvoiceNotes,
  fetchInvoiceNotesById,
  InvoiceNotes,
  PaymentInfo,
  previewInvoice
} from "../../../redux/slice/invoice/InvoiceSlice";
import { tokens } from "../../../theme";
import styled from "styled-components";
import { useAppDispatch } from "../../../redux/slice/redux-hooks";
import { SUCCESS_CODE } from "../../../service/ServiceConstants";
import { ArrowBack, AssignmentOutlined, ContentCopy } from "@mui/icons-material";
import Button from "../../common/Button";
import { convertBase64, priceFormatter, renderDynamicAgingColumn } from "../../../utils/Utility";
import PDFViewer from "../../common/pdf/PDFViewer";
import { fetchOrderDocumentsByIds, getOrderItemsByInvoiceId, resetOrderDocumentInfo, useOrderSlice } from "../../../redux/slice/order/OrderSlice";
import CustomIconButton from "../../common/CustomIconButton";
import CreditMemoTable from "../../common/CreditMemoTablePopup";
import CustomCommentPreview from "../../common/CustomCommentPreview";
import { useNavigation } from "../../../hooks/useNavigation";
import { useSelector } from "react-redux";
import { RootState } from "../../../redux/store";
import { selectIsTokenAvailable, selectIsValueFoundInToken, useAuthSlice } from "../../../redux/slice/authSlice";
import { INVOICE_ACTION, INVOICE_DETAIL_VIEW_ACTIONS, ORDER_AVAILABILITY, PAYMENT_STATUS, USER_ROLE_ENTITY_NAMES } from "../../../constants/applicationConstants";
import { useForm } from "react-hook-form";
import { setSingleAlertObj, SingleAlertInfo } from "../../../redux/slice/commonSlice";
import CustomProgress from "../../common/CustomProgress";
import SpeechToTextPopup from "../../common/SpeechToTextPopup";
import { getSalesDetailsByOrderId, resetSaleDetailsStatus, useSaleDetailsSlice } from "../../../redux/slice/saleDetails/SaleDetailsSlice";
import ExcelDetailsPopup from "../orders/ExcelDetailsPopup";

const MigrateBillingDetail = () => {
  const location = useLocation();
  const detailQueryParams = new URLSearchParams(location.search);
  const resolvedInvoiceId: number | undefined =
    location.state?.invoiceId ?? (Number(detailQueryParams.get("invoiceId")) || undefined);
  const resolvedOrderId: number | undefined =
    location.state?.orderId ?? (Number(detailQueryParams.get("orderId")) || undefined);
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const { navigate } = useNavigation();
  const colors = tokens(theme.palette.mode);
  const [selectedInvoice, setSelectedInvoice] = useState<InvoiceDetailDTO>();
  const [showPDF, setShowPDF] = useState<{ isShow: boolean, productName: string, fileName: string, content?: string }>({ isShow: false, productName: "", fileName: "", content: "" })
  //CS-1191 - Strat
  const canAccessEditInvoiceAction = useSelector((state: RootState) => selectIsValueFoundInToken("canAccessInvoiceActions", INVOICE_ACTION.EDIT_INVOICE)(state));
  //CS-1191 - End
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { orderItemsListInfo, orderDocumentInfo } = useOrderSlice();
  const [showCreditMemoPopup, setShowCreditMemoPopup] = useState(false);
  const { recordCreditMemo } = useInvoiceSlice();
  //CS-1212 - Start
  const { handleSubmit, setValue, getValues, control } = useForm<InvoiceNotes>();
  const [selectedFileName, setSelectedFileName] = useState<string | null>(null);
  const [pdfContent, setPDFContent] = useState<string>("");
  //CS-1212 - End
  const [invoiceAmount, setInvoiceAmount] = useState(0);
  const [shippingCharges, setShippingCharges] = useState(0);
  const [total, setTotal] = useState(0);
  const [paidAmount, setPaidAmount] = useState(0);
  const [remainingAmount, setRemainingAmount] = useState(0);
  const [isVoicePopupOpen, setIsVoicePopupOpen] = useState(false);
  const { SalesDetailsInfo } = useSaleDetailsSlice();
  const [showExcelPopup, setShowExcelPopup] = useState(false);
  const { userInfo } = useAuthSlice();
  const canViewExcelData = useSelector((state: RootState) => selectIsTokenAvailable("canViewExcelData")(state));

  useEffect(() => {
    const invoiceId = resolvedInvoiceId;
    const orderId = resolvedOrderId;
    if (invoiceId) {
      setInvoiceDetails(invoiceId);
      setValue("xrefId", String(invoiceId)); //CS-1212
      dispatch(fetchInvoiceNotesById(invoiceId)); //CS-1212
    };
    if (orderId) {
      dispatch(getSalesDetailsByOrderId(orderId));
    }
    resetData(); //CS-1212
  }, []);

  //Unmount
  useEffect(() => () => {
    dispatch(resetSaleDetailsStatus())
  }, []);

  const setInvoiceDetails = async (invoiceId: number) => {
    await dispatch(getCreditMemoDetailsById(invoiceId))
    const response = await dispatch(getInvoiceById({invoiceId: invoiceId, isSaveAuditLog: false}));
    if (response?.payload?.code === SUCCESS_CODE && response?.payload?.data) {
      setSelectedInvoice(response.payload.data);

      dispatch(getOrderItemsByInvoiceId(invoiceId));
    }
  };



  const paymentListData = useMemo(() => {
    const rows: PaymentInfo[] = []
    selectedInvoice?.payment?.paymentInfo?.map(payment => {
      rows.push(payment);
    });
    return rows;
  }, [selectedInvoice]);

  useEffect(() => {
    if (selectedInvoice?.payment?.totalAmount) {
      setPaidAmount(parseFloat(selectedInvoice.payment.totalAmount));
    };

    if (selectedInvoice?.shippingCharges) {
      setShippingCharges(selectedInvoice.shippingCharges);
    }

    if (selectedInvoice?.invoiceAmount) {
      setInvoiceAmount(selectedInvoice?.invoiceAmount)
      const totalAmount = selectedInvoice?.invoiceAmount + selectedInvoice?.shippingCharges;
      setTotal(totalAmount)
    };

    setRemainingAmount(selectedInvoice?.payment?.remainingAmount ??
      selectedInvoice?.netCogsAmount ??
      0);
  }, [selectedInvoice]);

  const handleOrderListNavigate = (orderNumber: String) => {
    navigate("/order_list", { state: { orderNumber: orderNumber } });
  };

  const handleOrderDetailsNavigate = (data: any) => {
    const orderData = {
      orderId: data?.orderId,
      orderNumber: data?.orderNumber,
      productName: data?.productName,
      practiceName: data?.practiceName,
      po: data?.poNumber,
      dateOfService: data?.dateOfService,
      shippingCarrier: data?.shippingCarrier,
      trackingNumber: data?.trackingNumber,
      orderStatus: data?.orderStatus,
      provider: data?.providerName,
      patientName: data?.patientName,
      manufacturerName: data?.mfgName,
      orderDocumentId: data?.orderDocumentId,
      productCategory: data?.productCategory
    }
    if (orderData.orderNumber.startsWith("M")) {
      navigate("/migrate_details/1", { state: { orderDetails: orderData } });
    } else {
      navigate("/details/1", { state: { orderDetails: orderData } });
    }

  }
  const handleDownloadMFGInvoice = async (data: any) => {
    let documentId = data?.invoiceDocumentId;
    const actionResult = await dispatch(fetchOrderDocumentsByIds(documentId));
    const documentData = actionResult.payload;
    if (documentData && documentData.content) {
      setShowPDF({
        isShow: true,
        productName: `Invoice-${data?.productName}`,
        fileName: `MFG_${data?.mfgInvoiceNumber}`,
      });
    };
  }

  //CS-1322 - Function to genrate the invoice pdf
  const handleGenrateInvoice = async (invoiceId: number) => {
    const actionResult = await dispatch(previewInvoice({ invoiceId, interceptorOption: { skipErrorPopup: true } }));
    if (actionResult.payload.code === SUCCESS_CODE) {
      const documentData = actionResult.payload;
      if (documentData.data && documentData.data.content) {
        setShowPDF({
          isShow: true,
          productName: `Invoice-${selectedInvoice?.productName}`,
          content: documentData.data.content,
          fileName: `MFG_${selectedInvoice?.mfgInvoiceNumber}`,
        });
      }
    } else {
      dispatch(setSingleAlertObj({
        message: actionResult.payload.message,
        alertType: "error",
      }));
    }
  }

  const customPaymentCommentsComponent = (paymentInfo: {
    fileId?: number;
    paymentAmount?: number;
    paymentComments: any;
    paymentDate?: string;
    paymentDocumentId?: number;
    paymentId?: number;
  }) => {
    return (
      <>
        <Grid item>
          {paymentInfo?.paymentComments && (
            <CustomCommentPreview
              title="Payment Comments"
              commentBody={paymentInfo?.paymentComments}
            />
          )}
        </Grid>
      </>
    );
  };

  const orderItemStatus = useCallback((row: any) => {
    if (row?.tissueTrackingId) {
      return `Applied (#${row.tissueTrackingId})`;
    } else if (row?.productReturnId) {
      // return `Returned (#${row.productReturnId})`;
      //CS-1164 START
      if (row.productReturnStatus === ORDER_AVAILABILITY.RETURN_INITIATED) {
        return  `Return Initiated (#${row.productReturnId})`;
      } else if (row.productReturnStatus === ORDER_AVAILABILITY.RETURN_PENDING) {
        return  `Return Pending (#${row.productReturnId})`;
      } else if (row.productReturnStatus === ORDER_AVAILABILITY.RETURNED) {
        return `Returned (#${row.productReturnId})`;
      } else if (row.productReturnStatus === ORDER_AVAILABILITY.RETURN_REJECTED) {
        // return `Return Rejected (#${row.productReturnId})`;
        return `Available (Return Rejected)`;
        }
      //CS-1164 END
    } else if (row?.invoiceId) {
      return "Available";
    }
    return "Pending";
  }, []);

  //CS-1212 - Start
  const removeDataUriPrefix = (dataUri: string): string => {
    const base64String = dataUri.split(",")[1];
    return base64String || "";
  };

  const handleNotDocumentFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    try {
      const files = event.target.files;
      if (files && files.length > 0) {
        const file = files[0];
        const base64File = await convertBase64(file);

        setValue("attachment", removeDataUriPrefix(String(base64File)));
        setValue("documentName", file.name);
        setValue("fileType", file.type);
        setValue("noteType", "INVOICE ADDITIONAL NOTE");
        setValue("xrefTable", "INVOICE");
        setSelectedFileName(file.name);
      } else {
        console.error("No files selected or invalid input.");
      }
    } catch (error) {
      console.error("Error processing the file:", error);
    }
  };

  const resetData = () => {
    setValue("attachment", "");
    setValue("xrefTable", "INVOICE");
    setValue("noteType", "INVOICE ADDITIONAL NOTE");
    setValue("content", "");
    handleRemoveFile();
  };

  const handleRemoveFile = () => {
    setSelectedFileName(null);
    setValue("attachment", "");
  };

  const onSubmit = async (data: InvoiceNotes) => {
    setValue("xrefId", String(resolvedInvoiceId ?? ""));
    try {
      const response = await dispatch(uploadInvoiceNotes(data));
      if (
        response.payload &&
        response.payload.message === "Note is added successfully"
      ) {
        let alertMessage = "Note saved Successfully!";
        const alertObj: SingleAlertInfo = {
          message: alertMessage,
          alertType: "success",
        };
        dispatch(setSingleAlertObj(alertObj));
        dispatch(fetchInvoiceNotesById(resolvedInvoiceId ?? 0));
        resetData();
      }
    } catch (error) {
      console.log("Error saving note:", error);
    }
  };

  const handleActionPreview = async (row: any, type: string) => {
    const documentId = type === "note" ? row.attachementId : row.documentId;
    if (documentId) {
      const actionResult = await dispatch(
        fetchOrderDocumentsByIds(documentId)
      );
      const documentData = actionResult.payload;
      if (documentData && documentData.content) {

        setPDFContent(documentData.content);
        console.log("documentData", documentData);
        console.log("pdfContent", pdfContent);
        setShowPDF({
          isShow: true,
          productName: "Invoice - " + row.auditUserName,
          fileName: documentData.documentName,
        });
      } else {
        dispatch(
          setSingleAlertObj({
            message: "Requested Document has not found under server location!",
            alertType: "info",
          })
        );
      }
    }
  };
  //CS-1212 - End

  const showExcelPopupData = (event: React.MouseEvent<HTMLElement>) => {
    setShowExcelPopup(true)
  };

  // Get the first element from the list
  const firstSalesDetail = useMemo(() => {
    return SalesDetailsInfo && SalesDetailsInfo.length > 0 ? SalesDetailsInfo[0] : null;
  }, [SalesDetailsInfo]);

  const allSerialNumbers = useMemo(() => {
    if (!firstSalesDetail?.serialNumber) return [];

    return firstSalesDetail.serialNumber
      .split(/\s+/) // Split by one or more whitespace characters
      .filter(Boolean); // Remove any empty strings
  }, [firstSalesDetail]);

  const allSizes = useMemo(() => {
    return firstSalesDetail?.prodSize
      ? firstSalesDetail.prodSize.split(/\s+/).map(size => size.trim()).filter(Boolean)
      : [];
  }, [firstSalesDetail]);

  const orderItemRow: any = useMemo(() => {
    let orderItems_: any = [];
    if (orderItemsListInfo) {
      orderItems_ = orderItemsListInfo
        .filter(item => item.invoiceId !== null)
        .map((item: any) => {
          return {
            orderItemId: item.orderItemId,
            productName: item.productName,
            size: item.size,
            quantity: item.quantity,
            units: item.units,
            serialNumber: item.serialNumber,
            orderStatus: orderItemStatus(item),
            cogs: item.hasSKUPrice ? item.extendedPrice : item.extendedPrice * item.units,
            productReturnId: item.productReturnId,
            tissueTrackingId: item.tissueTrackingId,
            invoiceId: item.invoiceId,
          };
        })
        .sort((a, b) => a.units - b.units);
    }
    return orderItems_;
  }, [orderItemsListInfo, location?.state?.orderDetails?.orderStatus]);


  return (
    <>
      {showCreditMemoPopup &&
        <CreditMemoTable
          open={showCreditMemoPopup}
          recordCreditMemo={recordCreditMemo}
          // userId={userID}
          onClose={() => {
            setShowCreditMemoPopup(false);
          }}
        />
      }
      {showPDF && <PDFViewer
        isOpen={showPDF.isShow}
        onClose={() => { setShowPDF({ isShow: false, productName: "", fileName: "", content: "" }); dispatch(resetOrderDocumentInfo()); }}
        title={showPDF.productName}
        fileName={showPDF.fileName}
        base64String={orderDocumentInfo?.content || showPDF.content || ""} // CS-1322
      />}
      {isVoicePopupOpen && (
        <SpeechToTextPopup
          open={isVoicePopupOpen}
          onClose={() => { setIsVoicePopupOpen(false) }}
          onSubmitVoice={(data: any) => setValue("content", data)}
        />
      )}
      <Box sx={{
        margin: {
          xs: "20px 10px",
          sm: "20px",
        },
      }}>
        {/* CS-1191 - Start */}
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Header title="Invoice Detail" />
          </Grid>
          {/* {selectedInvoice?.invoiceStatus !== "PAID" && (
          <Grid item xs={12} md={6} display="flex" flexDirection="column" sx={{ justifyContent: { xs: "flex-start", md: "flex-end" } }}>
            <Box display="flex" alignItems="center" sx={{ justifyContent: { xs: "flex-start", md: "flex-end" } }} gap={1} pb={1}>
              <Button
                color="info"
                onClick={() =>
                 navigate("/edit_invoice", { state: location?.state?.invoiceId })
                }
                style={{ display: `${hideElement(!canAccessEditInvoiceAction )}`}}
                startIcon={<ModeOutlined fontSize="small" />}
              >
                Update Invoice
              </Button>

            </Box>
          </Grid>
          )} */}
          <Grid item xs={12} md={6} display="flex" flexDirection="column" sx={{ justifyContent: { xs: "flex-start", md: "flex-end" } }}>
            <Box display="flex" alignItems="center" sx={{ justifyContent: { xs: "flex-start", md: "flex-end" } }} gap={1} pb={1}>
              {canViewExcelData && (
              <Button
                color="success"
                variant="outlined"
                sx={{ whiteSpace: "nowrap", mr: 1 }}
                onClick={showExcelPopupData}
                startIcon={<img src={require("../../../assets/excel_icon.png")} alt="Excel Icon" style={{ width: 24, height: 24 }} />}>
                View Excel Data
              </Button>
              )}
            </Box>
          </Grid>
        </Grid>
        {/* CS-1191 - End */}
        <Box mb={5}>

          <Grid container spacing={2}>
            <Grid item md={7} xs={12}>
              <Box>
                <Card>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Typography variant="h5" fontWeight="bold" gutterBottom color={colors.success[300]}>
                        Invoice Info
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color={colors.grey[500]}>Invoice</Typography>
                        <Typography variant="body1">
                        {selectedInvoice?.mfgInvoiceNumber}{" "}
                        {/* CS-1765 - Use top-level purchaseAndBill from getInvoiceById response instead of manufactureCustomConfig */}
                        {selectedInvoice?.purchaseAndBill === "Y" ? (
                          <CustomIconButton
                            tooltipPlacement="top"
                            tooltipMessage="Preview"
                            color="info"
                            size="small"
                            variant="outlined"
                            onClick={() => handleGenrateInvoice(selectedInvoice?.invoiceId)}
                          >
                            <AssignmentOutlined fontSize="small" />
                          </CustomIconButton>
                        ) : (<>
                          {selectedInvoice?.invoiceDocumentId && (<>
                            <CustomIconButton
                              tooltipPlacement="top"
                              tooltipMessage="Preview"
                              color="info"
                              size="small"
                              variant="outlined"
                              onClick={() => handleDownloadMFGInvoice(selectedInvoice)}
                            >
                              <AssignmentOutlined fontSize="small" />
                            </CustomIconButton>
                          </>)}
                        </>)}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color={colors.grey[500]}>MFG</Typography>
                      <Typography variant="body1">{firstSalesDetail ? firstSalesDetail.mfg : "-"}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color={colors.grey[500]}>Status</Typography>
                      <Typography variant="body1" color={selectedInvoice?.invoiceStatus === "PAID" ? "#28a745" : "error"}>
                        {selectedInvoice?.invoiceStatus || "-"}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color={colors.grey[500]}>Product</Typography>
                      <Typography variant="body1">{firstSalesDetail ? firstSalesDetail.product : "-"}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color={colors.grey[500]}>Due Date</Typography>
                      <Typography variant="body1" color={selectedInvoice?.invoiceStatus === "PAID" ? "#28a745" : "error"}>
                        {selectedInvoice?.invoiceDueDate || "-"}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color={colors.grey[500]}>Practice</Typography>
                      <Typography variant="body1">{firstSalesDetail ? firstSalesDetail.accountPractice : "-"}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color={colors.grey[500]}>Aging</Typography>
                      <Typography variant="body1" color="error">
                        <Typography variant="body1">{renderDynamicAgingColumn(selectedInvoice)}</Typography>
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color={colors.grey[500]}>Order</Typography>
                      <Typography variant="body1">
                        {selectedInvoice?.orderNumber}{" "}
                        {selectedInvoice?.orderId && (
                          <Link
                            component="button"
                            underline="hover"
                            variant="body2"
                            onClick={() => handleOrderDetailsNavigate(selectedInvoice)}
                            color={colors.info[300]}
                          >
                            (Order Details)
                          </Link>
                        )}
                      </Typography>
                    </Grid>
                  </Grid>
                </Card>
              </Box>

              <Box mt={2} mb={2}>
                <Card>
                  <Box
                    sx={{
                      position: "relative",
                      borderRadius: 2,
                    }}
                  >
                    {!isMobile && (
                      <Divider
                        orientation="vertical"
                        sx={{
                          position: "absolute",
                          left: "50%",
                          top: 0,
                          bottom: 0,
                          transform: "translateX(-50%)",
                          borderRightWidth: 1,
                          borderRightStyle: "dashed",
                          borderColor: colors.grey[700],
                        }}
                        flexItem
                      />
                    )}
                    <Grid container height="100%">
                      <Grid item xs={12} md={6}>
                        <Box
                          sx={{
                            p: 2,
                          }}
                        >
                          <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                            mb={1}
                          >
                            <Typography
                              variant="h5"
                              fontWeight="bold"
                              sx={{ color: colors.success[300] }}
                            >
                              Serial Numbers
                            </Typography>

                            <Tooltip title="Copy all" placement="top" arrow>
                              <IconButton
                                size="small"
                                onClick={() =>
                                  navigator.clipboard.writeText(allSerialNumbers.join(", "))
                                }
                                disabled={allSerialNumbers.length === 0}
                              >
                                <ContentCopy fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>

                          <Box
                            sx={{
                              display: "flex",
                              flexWrap: "wrap",
                              gap: 1,
                              maxHeight: 220,
                              overflow: "auto",
                              pr: 0.5,
                            }}
                          >
                            {allSerialNumbers.length > 0 ? (
                              allSerialNumbers.map((sn, index) => (
                                <Tooltip
                                  title="Click to copy"
                                  key={`${sn}-${index}`}
                                  placement="top"
                                  arrow
                                >
                                  <Chip
                                    label={sn}
                                    size="small"
                                    clickable
                                    onClick={() => navigator.clipboard.writeText(sn)}
                                    variant="outlined"
                                    sx={{
                                      fontFamily:
                                        'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                                      borderColor: colors.grey[700],
                                    }}
                                  />
                                </Tooltip>
                              ))
                            ) : (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ fontStyle: 'italic' }}
                              >
                                No serial numbers available
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Box p={2}>
                          <Typography
                            variant="h5"
                            fontWeight="bold"
                            sx={{ color: colors.success[300], mb: 1 }}
                          >
                            Size Mix
                          </Typography>
                          {allSizes.length > 0 ? (
                            allSizes.map((size, index) => (
                              <Chip
                                key={`${size}-${index}`}
                                label={size}
                                size="small"
                                sx={{
                                  backgroundColor:
                                    theme.palette.mode === "dark"
                                      ? "#2C3E50"
                                      : colors.grey[700],
                                  padding: "1px 10px",
                                  fontWeight: "bold",
                                  border: "1px solid ",
                                  borderColor:
                                    theme.palette.mode === "dark"
                                      ? "#34495E"
                                      : colors.grey[700],
                                  mr: 1,
                                  mb: 1,
                                }}
                              />
                            ))
                          ) : (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ fontStyle: 'italic' }}
                            >
                              No size information available
                            </Typography>
                          )}
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                </Card>
              </Box>

              {/* {recordCreditMemo?.creditMemoDTOList?.length > 0 && (
                <Box mt={2}>
                  <Card>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Typography variant="h5" fontWeight="bold" gutterBottom color={colors.success[300]}>
                          Credits
                        </Typography>
                      </Grid>
                      {
                        recordCreditMemo?.creditMemoDTOList.map((credit: any) => (
                          <>
                            <Grid item xs={4}>
                              <Typography variant="body2" color={colors.grey[500]}>Date Of Credit</Typography>
                              <Typography variant="body1">{credit.dateOfCredit}</Typography>
                            </Grid>
                            <Grid item xs={4}>
                              <Typography variant="body2" color={colors.grey[500]}>Credit Amount</Typography>
                              <Typography variant="body1">{priceFormatter(credit.creditAmount)}</Typography>
                            </Grid>
                            <Grid item xs={4}>
                              <Typography variant="body2" color={colors.grey[500]}>Comments</Typography>
                              <Typography variant="body1">{credit.comments || '-'}</Typography>
                            </Grid>
                          </>
                        ))
                      }
                    </Grid>
                  </Card>
                </Box>
              )} */}
            </Grid>

            <Grid item md={5} xs={12}>
              <Box>
                <Card>
                  <Typography variant="h5" fontWeight="bold" sx={{ color: colors.success[300], mb: 1 }}>
                    Balance
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item md={12} xs={12}>
                      <Box>
                        <Grid container spacing={2}>
                          <Grid item xs={5}>
                            <Typography color={colors.grey[500]}>Invoice Amt</Typography>
                          </Grid>
                          <Grid item xs={7}>
                            <Typography>{invoiceAmount && priceFormatter(invoiceAmount) || priceFormatter(0)}</Typography>
                          </Grid>
                          <Grid item xs={5}>
                            <Typography color={colors.grey[500]}>Shipping Charges</Typography>
                          </Grid>
                          <Grid item xs={7}>
                            <Typography>{shippingCharges && priceFormatter(shippingCharges) || priceFormatter(0)}</Typography>
                          </Grid>
                          <Grid item xs={5}>
                            <Typography sx={{ fontWeight: "bold" }}>Total</Typography>
                          </Grid>
                          <Grid item xs={7}>
                            <Typography sx={{ color: "#5BE49B", fontWeight: "bold" }}>{total && priceFormatter(total) || priceFormatter(0)}</Typography>
                          </Grid>
                          <Grid item xs={5}>
                            <Typography color={colors.grey[500]}>Credits</Typography>
                          </Grid>
                          <Grid item xs={7}>
                            <Typography>
                              {selectedInvoice?.creditAmount ?
                                <Link
                                  component="button"
                                  underline="hover"
                                  variant="body2"
                                  onClick={() => setShowCreditMemoPopup(true)}
                                  color={colors.info[300]}
                                >
                                  {priceFormatter(selectedInvoice?.creditAmount)}
                                </Link>
                                : priceFormatter(0)}
                            </Typography>
                          </Grid>
                          <Grid item xs={5}>
                            <Typography color={colors.grey[500]}>Payments</Typography>
                          </Grid>
                          <Grid item xs={7}>
                            <Typography>{paidAmount && priceFormatter(paidAmount) || priceFormatter(0)}</Typography>
                          </Grid>
                          <Grid item xs={5}>
                            <Typography color={colors.grey[500]}>Remaining Balance</Typography>
                          </Grid>
                          <Grid item xs={7}>
                            <Typography>
                              {remainingAmount && priceFormatter(remainingAmount) || priceFormatter(0)}
                            </Typography>
                          </Grid>

                          <Grid item xs={12}>
                            <CustomProgress
                              filledValue={paidAmount}
                              total={(total - (selectedInvoice?.creditAmount || 0))}
                              color="warning"
                              height={12}
                            />
                          </Grid>
                        </Grid>
                      </Box>
                    </Grid>
                  </Grid>
                </Card>
              </Box>

              {paymentListData.length > 0 && (
                <Box mt={2}>
                  <Card>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Typography variant="h5" fontWeight="bold" gutterBottom color={colors.success[300]}>
                          Payments
                        </Typography>
                      </Grid>
                      {paymentListData.map((payment: any) => (
                        <>
                          <Grid item xs={4}>
                            <Typography variant="body2" color={colors.grey[500]}>Payment Date</Typography>
                            <Typography variant="body1">{payment.paymentDate}</Typography>
                          </Grid>
                          <Grid item xs={4}>
                            <Typography variant="body2" color={colors.grey[500]}>Payment Amount</Typography>
                            <Typography variant="body1"
                              sx={payment.paymentStatus === PAYMENT_STATUS.VOID ? {
                                textDecoration: "line-through red",
                                textDecorationThickness: "2px",
                              } : {}}
                            >{priceFormatter(payment.paymentAmount)}</Typography>
                          </Grid>
                          <Grid item xs={4}>
                            <Typography variant="body2" color={colors.grey[500]}>Status</Typography>
                            <Typography variant="body1" sx={payment.paymentStatus === PAYMENT_STATUS.VOID
                              ? { color: "#dc3545" } : {}}>{payment.paymentStatus}</Typography>
                          </Grid>
                        </>
                      ))}
                    </Grid>

                  </Card>
                </Box>
              )}

            </Grid>
          </Grid>
        </Box>
        <Grid display="flex" justifyContent="flex-end">
          <Grid item>
            <Button
              color="info"
              startIcon={<ArrowBack />}
              onClick={() => navigate(-1)}
              sx={{ mt: 2, mb: 4 }}
            >
              Back
            </Button>
          </Grid>
        </Grid>
        <ExcelDetailsPopup onClose={() => setShowExcelPopup(false)} open={showExcelPopup} rows={SalesDetailsInfo} />
      </Box>
    </>
  );
};

export default MigrateBillingDetail;
